import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { Grid, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, Chip } from '@mui/material';
import i18next from 'i18next';
import { WLOfferHistoryDetailServiceOutput, WLOfferHistoryListShortFragment } from '__generated__/graphql';
import { formatDate } from 'components/DateTime';
import { formatOfferDayLabel } from 'helper/formatOfferDayLabel';
import { offerHistoryServiceDisplayName } from 'helper/quickPriceSkuDisplayName';

interface AddonChangesSummaryProps {
  history: WLOfferHistoryListShortFragment[];
  startDate?: string | null;
  hotelId?: number | null;
  /** When true (default), only show addons fully removed (newCount === 0). */
  deletionsOnly?: boolean;
}

/** History may store absolute newCount (0) or legacy delta-as-newCount (-1). */
const effectiveNewCount = (detail: WLOfferHistoryDetailServiceOutput): number => {
  const previous = detail.previousCount ?? 0;
  const next = detail.newCount ?? 0;
  if (next < 0) {
    return Math.max(0, previous + next);
  }
  return next;
};

const isRemovedAddon = (detail: WLOfferHistoryDetailServiceOutput): boolean =>
  (detail.previousCount ?? 0) > 0 && effectiveNewCount(detail) === 0;

/** Participant attendance batches are not addon/room count edits from the client form. */
const isParticipantChangesBatch = (entry: WLOfferHistoryListShortFragment): boolean => {
  const info = entry.extraInfo || '';
  if (info.includes('participant_changes')) {
    return true;
  }
  try {
    const parsed = JSON.parse(info);
    return parsed?.reason === 'participant_changes';
  } catch {
    return false;
  }
};

/** SERVICE_COUNT_EDITED batches from addon/room changes (excludes participant recalculation). */
const isAddonOrRoomCountBatch = (entry: WLOfferHistoryListShortFragment): boolean =>
  entry.status === 'SERVICE_COUNT_EDITED' && !isParticipantChangesBatch(entry);

/** Same customer save often writes rooms then addons as two SERVICE_COUNT_EDITED rows seconds apart. */
const SIBLING_BATCH_WINDOW_MS = 60_000;

const changeDetailKey = (detail: WLOfferHistoryDetailServiceOutput): string =>
  `${detail.dayId ?? -1}:${detail.sku ?? ''}:${detail.serviceName ?? ''}`;

/**
 * Collects removals/changes from the latest addon/room count save, including sibling batches
 * (seminar rooms + packages written in the same updateEventAttendance transaction).
 */
const findLatestAddonServiceHistory = (
  history: WLOfferHistoryListShortFragment[],
  deletionsOnly: boolean,
): { entry: WLOfferHistoryListShortFragment; changes: WLOfferHistoryDetailServiceOutput[] } | null => {
  const batches = _(history)
    .orderBy(h => h.createdAt, 'desc')
    .filter(h => isAddonOrRoomCountBatch(h) && (h.serviceDetails?.length ?? 0) > 0)
    .value();

  if (batches.length === 0) {
    return null;
  }

  const latestBatch = batches[0];
  const latestAt = moment(latestBatch.createdAt);
  const siblingBatches = batches.filter(batch => Math.abs(moment(batch.createdAt).diff(latestAt)) <= SIBLING_BATCH_WINDOW_MS);

  const changes = _(siblingBatches)
    .flatMap(batch => batch.serviceDetails ?? [])
    .filter(detail => !deletionsOnly || isRemovedAddon(detail))
    .uniqBy(changeDetailKey)
    .value();

  if (changes.length === 0) {
    return null;
  }

  return { entry: latestBatch, changes };
};

/** Whether the latest addon/room service history save has rows to show (respects deletionsOnly). */
export function hasRecentAddonRemovals(
  history: WLOfferHistoryListShortFragment[],
  deletionsOnly = true,
): boolean {
  return findLatestAddonServiceHistory(history, deletionsOnly) !== null;
}

/**
 * Displays addon/room removals from the most recent customer service-count save
 * (merges sibling room + addon SERVICE_COUNT_EDITED batches).
 */
export default function AddonChangesSummary({ history, startDate, hotelId, deletionsOnly = true }: AddonChangesSummaryProps) {
  const result = findLatestAddonServiceHistory(history, deletionsOnly);
  if (!result) {
    return null;
  }

  const { changes: serviceChanges } = result;

  const changesByDay = _(serviceChanges)
    .groupBy(change => change.dayId ?? -1)
    .mapValues(changes => _.sortBy(changes, c => c.serviceName))
    .value();

  const getDayLabel = (dayId: number): string => {
    if (dayId === -1) {
      return i18next.t('offer-input-header-preday');
    }
    const dayLabel = formatOfferDayLabel(dayId);
    if (startDate) {
      const dayDate = formatDate(moment.utc(startDate, 'YYYY-MM-DD').add(dayId, 'days').toDate());
      return `${dayLabel} - ${dayDate}`;
    }
    return dayLabel;
  };

  const sortedDayIds = Object.keys(changesByDay)
    .map(Number)
    .sort((a, b) => {
      if (a === -1) return -1;
      if (b === -1) return 1;
      return a - b;
    });

  return (
    <Grid container spacing={2}>
      {sortedDayIds.map(dayId => (
        <Grid item xs={12} key={dayId}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
              {getDayLabel(dayId)}
            </Typography>

            <TableContainer>
              <Table size="small" sx={{ '& td': { py: 0.75 } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                      {i18next.t('offer-history-addon-name')}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: '20%' }}>
                      {i18next.t('offer-history-previous-count')}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: '20%' }}>
                      {i18next.t('offer-history-new-count')}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: '25%' }}>
                      {i18next.t('offer-history-change-status')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changesByDay[dayId].map((change, idx) => (
                    <TableRow key={idx} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {offerHistoryServiceDisplayName(change, hotelId)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {change.previousCount ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {effectiveNewCount(change)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center">
                          <Chip
                            label={i18next.t('offer-addon-change-removed')}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}
